import { useRef } from "react";
import { DollarSignIcon } from "lucide-react";

import { Row } from "@/components";
import {
  HexagonIconBadge,
  MetricLeadersHoverCard,
  RevenueRacetrack,
  RevenueThroneScene,
  RevenueThroneSummary,
} from "@/components/leaderboard";
import { Card, CardContent } from "@/components/ui/card";
import {
  useInactiveUserIds,
  useTaggableUsers,
} from "@/contexts/TaggableUsersContext";
import { useGetLeaderboard } from "@/hooks/queries/useGetLeaderboard";
import { useGetPets } from "@/hooks/queries/useGetPets";
import { useGetTeamGoal } from "@/hooks/queries/useGetTeamGoal";
import { REVENUE_GRADIENT } from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { leaderboardMetrics } from "@/lib/leaderboardMetrics";
import {
  leaderboardGoal,
  type RacetrackGoalScope,
} from "@/lib/leaderboardGoal";
import { stafferPets } from "@/lib/stafferPets";
import { teamGoals } from "@/lib/teamGoals";
import { cn } from "@/lib/utils";

import {
  TeamGoalField,
  UserRole,
  type LeaderboardQueryParams,
  type PetAppearance,
  type TeamGoal,
} from "@/types";

const RACETRACK_MIN_CARD_HEIGHT_PX = 409;

// Per-staffer revenue goal for the finish line: an even share of the month's
// team goal, scaled to a week when the selected period is weekly.
const perStafferRevenueGoal = (
  teamGoal: TeamGoal | null | undefined,
  scope: RacetrackGoalScope | null,
): number | undefined => {
  if (!teamGoal || teamGoal.numStaffers <= 0 || scope === null)
    return undefined;

  const monthly = teamGoals.perStafferGoal(
    teamGoal.revenueGoal,
    teamGoal.numStaffers,
    TeamGoalField.REVENUE,
  );
  if (scope === "WEEK") {
    return teamGoals.weeklyGoal(monthly, TeamGoalField.REVENUE);
  }
  return monthly;
};

export const MostRevenueAndRacetrackContainer = ({
  queryParams,
  isFullViewport = true,
}: {
  queryParams: LeaderboardQueryParams;
  isFullViewport?: boolean;
}) => {
  /* React Hooks */
  const { users: taggableUsers } = useTaggableUsers();
  const inactiveUserIds = useInactiveUserIds();
  const { data: pets } = useGetPets({ select: stafferPets.toAppearances });
  const resolution = leaderboardGoal.resolvePeriod(queryParams);

  // The race always reflects the selected period; ranges wider than a month
  // just drop the goal (see leaderboardGoal) and race to the top staffer.
  const { data: raceData, isPlaceholderData } = useGetLeaderboard(queryParams);

  // Wait for staffer's  data to refresh before refreshing goal
  const committedResolution = useRef(resolution);
  if (!isPlaceholderData) committedResolution.current = resolution;
  const activeResolution = committedResolution.current;

  const { data: teamGoal } = useGetTeamGoal(
    activeResolution.goalMonth,
    activeResolution.goalYear,
  );

  /* Helpers */
  const activeStafferIds = new Set(
    taggableUsers
      .filter((u) => u.deletedAt === null && u.userRole === UserRole.STAFFER)
      .map((u) => u.id),
  );
  // Deactivated staffers still race if they earned revenue this period
  // (e.g. a placement closed before they were deleted).
  const racers = raceData
    ? raceData.filter((u) => activeStafferIds.has(u.userId) || u.revenue > 0)
    : [];

  // Each lane's racer — missing entry when the staffer hasn't adopted a pet.
  const petByUserId = new Map<number, PetAppearance>(
    (pets ?? []).map((pet) => [pet.userId, pet]),
  );

  const revenueGoalPerStaffer = perStafferRevenueGoal(
    teamGoal,
    activeResolution.goalScope,
  );

  const maxRevenue = Math.max(...racers.map((u) => u.revenue), 0);
  const leaders = racers
    .filter((u) => u.revenue === maxRevenue && maxRevenue > 0)
    .sort((a, b) => a.firstName.localeCompare(b.firstName));

  /* Render */
  const renderRevenueHoverBadge = (backdropColor?: string) => (
    <MetricLeadersHoverCard
      title="Most Revenue"
      icon={DollarSignIcon}
      gradient={REVENUE_GRADIENT}
      leaders={leaderboardMetrics.topN(racers, "revenue", 3)}
      valueOf={(u) => u.revenue}
      format={formatting.currency}
      badgeBackdropColor={backdropColor}
    >
      <Row className="cursor-default">
        <HexagonIconBadge
          icon={DollarSignIcon}
          gradient={REVENUE_GRADIENT}
          backdropColor={backdropColor}
        />
      </Row>
    </MetricLeadersHoverCard>
  );

  const renderRacetrack = () => (
    <RevenueRacetrack
      users={racers}
      revenueGoalPerStaffer={revenueGoalPerStaffer}
      goalScope={activeResolution.goalScope}
      isWinter
      isFitToHeight={isFullViewport}
      petByUserId={petByUserId}
      inactiveUserIds={inactiveUserIds}
    />
  );

  const render = () => {
    return (
      <Card
        className={cn(
          "relative flex flex-col overflow-hidden border-0 bg-slate-950 shadow-sm",
          isFullViewport ? "rounded-none" : "rounded-3xl",
        )}
        style={{
          height: isFullViewport ? "100svh" : undefined,
          minHeight: isFullViewport ? undefined : RACETRACK_MIN_CARD_HEIGHT_PX,
        }}
      >
        <RevenueThroneScene leaders={leaders} petByUserId={petByUserId} />
        <CardContent className="relative z-[1] flex min-h-0 flex-1 flex-col p-0">
          <Row
            className={cn(
              "items-start gap-3 px-7 pt-6",
              isFullViewport ? "pr-52" : "justify-between",
            )}
          >
            <RevenueThroneSummary leaders={leaders} maxRevenue={maxRevenue} />
            {renderRevenueHoverBadge("#cbd5e1")}
          </Row>
          <Row
            className={cn(
              "mt-auto bg-gradient-to-b from-transparent via-black/20 to-black/45 px-12 pb-4 pt-8",
              // Keep the lanes on the floor below the throne and its staircase.
              isFullViewport && "h-[62%] min-h-0 shrink-0 px-8 pt-6",
            )}
          >
            {renderRacetrack()}
          </Row>
        </CardContent>
      </Card>
    );
  };

  return render();
};
